import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSearch } from 'react-icons/fi';
import { FaFileAlt } from 'react-icons/fa';
import { DocumentTemplate, DocumentTypeModel } from '../../types/document';
import DocumentEditor from '../../components/DocumentEditor';

const DocumentTemplates: React.FC = () => {
	const { token } = useAuth();
	const navigate = useNavigate();
	const previewEditorRef = useRef<any>(null);
	const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
	const [filteredTemplates, setFilteredTemplates] = useState<DocumentTemplate[]>([]);
	const [documentTypes, setDocumentTypes] = useState<DocumentTypeModel[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedType, setSelectedType] = useState('all');
	const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive' | 'draft'>('all');
	const [previewTemplate, setPreviewTemplate] = useState<DocumentTemplate | null>(null);

	// Fetch document types
	useEffect(() => {
		fetchDocumentTypes();
	}, [token]); // Fetch when token is available

	// Fetch templates
	useEffect(() => {
		fetchTemplates();
	}, [token]);

	// Load template content into preview editor when modal opens
	useEffect(() => {
		if (previewTemplate && previewEditorRef.current?.documentEditor) {
			const loadContent = () => {
				if (previewTemplate.content_template) {
					try {
						console.log('[Preview] Loading template content:', previewTemplate.name);
						
						// Populate template with sample data
						const populatedContent = populateTemplateWithSampleData(previewTemplate.content_template);
						
						// Load content
						previewEditorRef.current.documentEditor.open(populatedContent);
						
						// Set zoom to fit page width
						setTimeout(() => {
							if (previewEditorRef.current?.documentEditor) {
							previewEditorRef.current.documentEditor.fitPage('FitPageWidth');
						}
					}, 500);
					} catch (error) {
						console.error('[Preview] Error loading template:', error);
					}
				}
			};
			
			// Give the editor a moment to fully initialize
			const timer = setTimeout(loadContent, 200);
			return () => clearTimeout(timer);
		}
	}, [previewTemplate]);

	// Filter templates
	useEffect(() => {
		let filtered = templates;

		// Filter by type
		if (selectedType !== 'all') {
			filtered = filtered.filter(t => t.document_type === selectedType);
		}

		// Filter by status
		if (selectedStatus === 'active') {
			filtered = filtered.filter(t => t.is_active === true);
		} else if (selectedStatus === 'inactive') {
			filtered = filtered.filter(t => t.is_active === false);
		}
		// Note: 'draft' status would need a draft field in the model

		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter(t =>
				t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				t.description?.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		setFilteredTemplates(filtered);
	}, [templates, selectedType, selectedStatus, searchTerm]);

	const fetchDocumentTypes = async () => {
		if (!token) return;
		
		try {
			const response = await apiCall('/documents/document-types/', {
				method: 'GET',
			}, token);

			if (response.ok) {
				const data = await response.json();
				const types = data.results || data || [];
				setDocumentTypes(types);
			} else {
				console.error('Failed to fetch document types:', response.statusText);
			}
		} catch (error) {
			console.error('Error fetching document types:', error);
		}
	};

	const fetchTemplates = async () => {
		if (!token) return;
		
		try {
			setLoading(true);
			const response = await apiCall('/correspondence/document-templates/', {
				method: 'GET',
			}, token);

			if (response.ok) {
				const data = await response.json();
				setTemplates(data.results || data || []);
			}
		} catch (error) {
			console.error('Error fetching templates:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleAdd = () => {
		navigate('/documents/templates/editor');
	};

	const handleEdit = (template: DocumentTemplate) => {
		navigate('/documents/templates/editor', { state: { template } });
	};

	const handleDelete = async (id: string) => {
		if (!token) return;
		if (!window.confirm('Are you sure you want to delete this template?')) {
			return;
		}

		try {
			const response = await apiCall(`/correspondence/document-templates/${id}/`, {
				method: 'DELETE',
			}, token);

			if (response.ok) {
				fetchTemplates();
			}
		} catch (error) {
			console.error('Error deleting template:', error);
			alert('Failed to delete template');
		}
	};

	const populateTemplateWithSampleData = (content: string): string => {
		const today = new Date();
		const dateShort = today.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
		
		// Format long date with ordinal suffix (27th December, 2025)
		const day = today.getDate();
		const getOrdinalSuffix = (n: number) => {
			const s = ['th', 'st', 'nd', 'rd'];
			const v = n % 100;
			return s[(v - 20) % 10] || s[v] || s[0];
		};
		const monthLong = today.toLocaleDateString('en-AU', { month: 'long' });
		const year = today.getFullYear();
		const dateLong = `${day}${getOrdinalSuffix(day)} ${monthLong}, ${year}`;
		
		const currentTime = today.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
		
		// Sample data mapping
		const sampleData: { [key: string]: string } = {
			// Date & Time
			'[LetterDateShort]': dateShort,
			'[LetterDateLong]': dateLong,
			'[CurrentTime]': currentTime,
			
			// Provider (use actual provider from template or default)
			'[ProviderSalutation]': 'Dr',
			'[ProviderFirstName]': previewTemplate?.provider_name?.split(' ')[0] || 'Paul',
			'[ProviderLastName]': previewTemplate?.provider_name?.split(' ').slice(1).join(' ') || 'Verrills',
			'[ProviderFullName]': previewTemplate?.provider_name ? `Dr ${previewTemplate.provider_name}` : 'Dr Paul Verrills',
			'[ProviderTitle]': 'MBBS, FRACP, FFPMANZCA',
			'[ProviderSpecialty]': 'Pain Medicine',
			'[ProviderPhone]': '(02) 9999 8888',
			'[ProviderEmail]': 'info@clinic.com.au',
			
			// Recipient
			'[RecipientSalutation]': 'Dr',
			'[RecipientFirstName]': 'Jane',
			'[RecipientLastName]': 'Doe',
			'[RecipientFullName]': 'Dr Jane Doe',
			'[RecipientOrganization]': 'Sydney General Hospital',
			'[RecipientAddress]': '123 Medical Street, Sydney NSW 2000',
			'[RecipientPhone]': '(02) 9876 5432',
			'[RecipientMobile]': '0412 345 678',
			'[RecipientFax]': '(02) 9876 5433',
			'[RecipientEmail]': 'recipient@hospital.com.au',
			'[RecipientSpecialty]': 'General Practice',
			
			// Patient
			'[PatientSalutation]': 'Mr',
			'[PatientFirstName]': 'Michael',
			'[PatientLastName]': 'Johnson',
			'[PatientFullName]': 'Mr Michael Johnson',
			'[PatientDOB]': '15/03/1975',
			'[PatientAge]': '49',
			'[PatientMRN]': 'MRN123456',
			'[PatientAddress]': '456 Patient Lane, Melbourne VIC 3000',
			'[PatientPhone]': '0400 123 456',
			'[PatientEmail]': 'patient@email.com',
			'[PatientMedicare]': '2123 45678 9',
			
			// Facility
			'[FacilityName]': 'Templar Edge Medical Centre',
			'[FacilityAddress]': '789 Healthcare Drive, Brisbane QLD 4000',
			'[FacilityPhone]': '(07) 3333 4444',
			'[FacilityFax]': '(07) 3333 4445',
			
			// Document
			'[DocumentTitle]': 'Medical Letter',
			'[DocumentType]': previewTemplate?.document_type_name || 'General Letter',
			'[DocumentID]': 'DOC-2025-001',
			'[LetterBody]': 'This is where the AI-generated letter content will appear. The body of the letter will be inserted here when using the Generate Letter feature.',
		};
		
		let populatedContent = content;
		
		// Replace all placeholders with sample data
		Object.entries(sampleData).forEach(([placeholder, value]) => {
			const regex = new RegExp(placeholder.replace(/[\[\]]/g, '\\$&'), 'g');
			populatedContent = populatedContent.replace(regex, value);
		});
		
		return populatedContent;
	};

	const handleView = (template: DocumentTemplate) => {
		setPreviewTemplate(template);
	};

	const getTypeLabel = (typeId: string) => {
		const docType = documentTypes.find(dt => dt.id.toString() === typeId);
		return docType?.name || typeId;
	};

	return (
		<div className="p-6">
			{/* Header */}
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
					<FaFileAlt className="text-primary" />
					Document Templates
				</h1>
				<button
					onClick={handleAdd}
					className="btn btn-primary gap-2"
				>
					<FiPlus className="w-5 h-5" />
					Add Template
				</button>
			</div>

			{/* Search and Filter Bar */}
			<div className="bg-base-200 rounded-lg p-4 mb-6">
				<div className="flex items-center gap-6">
					{/* Search Input */}
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium whitespace-nowrap">Search:</span>
						<input
							type="text"
							placeholder="Type name of template"
							className="input input-bordered w-64"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div>

					{/* Document Type Filter */}
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium whitespace-nowrap">Document Type:</span>
						<select
							className="select select-bordered w-48"
							value={selectedType}
							onChange={(e) => setSelectedType(e.target.value)}
						>
							<option value="all">All Types</option>
							{documentTypes.map(type => (
								<option key={type.id} value={type.id.toString()}>
									{type.name}
								</option>
							))}
						</select>
					</div>

					{/* Status Filter */}
					<div className="flex items-center gap-4">
						<span className="text-sm font-medium whitespace-nowrap">Status:</span>
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="all"
								checked={selectedStatus === 'all'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-primary"
							/>
							<span className="text-sm">All</span>
						</label>
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="active"
								checked={selectedStatus === 'active'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-primary"
							/>
							<span className="text-sm">Active</span>
						</label>
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="inactive"
								checked={selectedStatus === 'inactive'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-primary"
							/>
							<span className="text-sm">Inactive</span>
						</label>
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="draft"
								checked={selectedStatus === 'draft'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-primary"
							/>
							<span className="text-sm">Draft</span>
						</label>
					</div>
				</div>
			</div>

			{/* Templates Table */}
			<div className="bg-base-100 rounded-lg shadow-lg overflow-hidden">
				{loading ? (
					<div className="flex justify-center items-center p-12">
						<span className="loading loading-spinner loading-lg"></span>
					</div>
				) : filteredTemplates.length === 0 ? (
					<div className="text-center p-12">
						<p className="text-lg opacity-60">
							No {selectedType !== 'all' ? getTypeLabel(selectedType) + ' ' : ''}templates found
						</p>
						<p className="text-sm opacity-40 mt-2">
							{searchTerm || selectedType !== 'all'
								? 'Try adjusting your filters'
								: 'Click "Add Template" to create your first template'}
						</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="table table-zebra">
							<thead>
								<tr>
									<th>Name</th>
									<th>Type</th>
									<th>Provider</th>
									<th>Description</th>
									<th>Status</th>
									<th className="text-right">Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredTemplates.map((template) => (
									<tr key={template.id} className="hover">
										<td className="font-medium">{template.name}</td>
										<td>
											{template.document_type_name || template.document_type}
										</td>
										<td>
										{template.provider_name ? (
											<span className="text-sm">{template.provider_name}</span>
											) : (
												<span className="text-sm opacity-50">Shared</span>
											)}
										</td>
										<td className="max-w-md truncate opacity-70">
											{template.description || '-'}
										</td>
										<td>
											{template.is_active ? (
												<span className="badge badge-success badge-sm">Active</span>
											) : (
												<span className="badge badge-ghost badge-sm">Inactive</span>
											)}
										</td>
										<td>
											<div className="flex justify-end gap-2">
												<button
													onClick={() => handleView(template)}
													className="btn btn-ghost btn-sm btn-square"
													title="View"
												>
													<FiEye className="w-4 h-4" />
												</button>
												<button
													onClick={() => handleEdit(template)}
													className="btn btn-ghost btn-sm btn-square"
													title="Edit"
												>
													<FiEdit2 className="w-4 h-4" />
												</button>
												<button
													onClick={() => handleDelete(template.id)}
													className="btn btn-ghost btn-sm btn-square text-error"
													title="Delete"
												>
													<FiTrash2 className="w-4 h-4" />
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Results Count */}
			{!loading && (
				<div className="text-sm opacity-60 mt-4 text-center">
					Showing {filteredTemplates.length} of {templates.length} templates
				</div>
			)}

			{/* Preview Modal */}
			{previewTemplate && (
				<div className="modal modal-open">
					<div className="modal-box max-w-6xl h-[90vh] flex flex-col">
						<div className="flex justify-between items-center mb-4">
							<h3 className="font-bold text-lg">{previewTemplate.name}</h3>
							<button 
								onClick={() => setPreviewTemplate(null)}
								className="btn btn-sm btn-circle btn-ghost"
							>
								✕
							</button>
						</div>
						
						<div className="mb-4 grid grid-cols-2 gap-2 text-sm">
							<div><span className="opacity-60">Type:</span> {previewTemplate.document_type_name || previewTemplate.document_type}</div>
							<div><span className="opacity-60">Provider:</span> {previewTemplate.provider_name || 'Shared'}</div>
							<div><span className="opacity-60">Status:</span> {previewTemplate.is_active ? 'Active' : 'Inactive'}</div>
							{previewTemplate.description && (
								<div className="col-span-2"><span className="opacity-60">Description:</span> {previewTemplate.description}</div>
							)}
						</div>

						<div className="divider my-2"></div>

						<div className="flex-1 overflow-hidden">
							<DocumentEditor
								ref={previewEditorRef}
								height="100%"
								isReadOnly={true}
								enableToolbar={false}
								showPropertiesPane={false}
							/>
						</div>

						<div className="modal-action mt-4">
							<button onClick={() => setPreviewTemplate(null)} className="btn">Close</button>
							<button 
								onClick={() => {
									setPreviewTemplate(null);
									handleEdit(previewTemplate);
								}} 
								className="btn btn-primary"
							>
								Edit Template
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DocumentTemplates;
